import { Vector3, Curve3, Quaternion, TransformNode, AbstractMesh, LinesMesh, Color3 } from '@babylonjs/core';
import { Spline } from './Spline';
import { DebugRenderer } from '../core/DebugRenderer';

/**
 * SplineConstraint - Constrains entities to move along a 2.5D spline plane
 * 
 * Concept:
 * - The spline defines a curved "floor" path through 3D space
 * - Entities are projected to the closest point on this path
 * - Local coordinate system at each point:
 *   - Forward = tangent to spline (direction of path)
 *   - Up = perpendicular to tangent (jump direction)
 *   - Right = cross product (side-to-side on the plane)
 * 
 * Movement:
 * - X input = move along spline forward/back
 * - Y = height offset from spline (jumping)
 * - Entity always faces forward along spline
 */

export interface SplineProjection {
  // Position on spline (closest point)
  splinePoint: Vector3;
  
  // Normalized position along spline (0-1)
  t: number;
  
  // Final world position (spline point + height offset)
  worldPosition: Vector3;
  
  // Height above spline
  heightOffset: number;
  
  // Local coordinate frame at this point
  tangent: Vector3;    // Forward direction
  normal: Vector3;     // Up direction
  binormal: Vector3;   // Right direction
  
  // Rotation to align with spline
  rotation: Quaternion;
}

export class SplineConstraint {
  private spline: Spline;
  private debugPathLine: LinesMesh | null = null;
  private debugArrows: AbstractMesh[] = [];
  private markerInterval: number = 1.0; // meters

  constructor(curve: Curve3) {
    this.spline = new Spline(curve);
    this.createDebugVisualization();
  }

  /**
   * Get the total length of the spline
   */
  public getTotalLength(): number {
    return this.spline.getTotalLength();
  }

  /**
   * Get the number of points in the spline
   */
  public getSplinePointCount(): number {
    return this.spline.getPointCount();
  }

  /**
   * Project an entity onto the spline plane
   * Uses XZ coordinates to find closest spline point, preserves height offset (Y)
   * @param worldPosition - Position to project onto spline
   * @param lastClosestIndex - Optional hint for optimization (pass previous result for same entity)
   */
  project(worldPosition: Vector3, lastClosestIndex: number = 0): { projection: SplineProjection; closestIndex: number } {
    // Find closest point on spline using XZ distance (ignore height Y)
    const { point: splinePoint, t, closestIndex: newClosestIndex } = this.spline.findClosestPoint(worldPosition, lastClosestIndex);
    
    // Calculate local coordinate frame at this point
    const tangent = this.spline.getTangentAt(t);
    const normal = this.spline.getNormalAt(t);
    const binormal = this.spline.getBinormalAt(t);
    
    // Calculate height offset (project current position onto normal)
    const toPosition = worldPosition.subtract(splinePoint);
    const heightOffset = Vector3.Dot(toPosition, normal);
    
    // Final position = spline point + height offset along normal
    const worldPos = splinePoint.add(normal.scale(heightOffset));
    
    // Calculate rotation to align with spline
    const rotation = Quaternion.RotationQuaternionFromAxis(binormal, normal, tangent);
    
    return {
      projection: {
        splinePoint,
        t,
        worldPosition: worldPos,
        heightOffset,
        tangent,
        normal,
        binormal,
        rotation
      },
      closestIndex: newClosestIndex
    };
  }

  /**
   * Get point on spline at normalized t (0-1) using arc length
   */
  getPointAt(t: number): Vector3 {
    return this.spline.getPointAt(t);
  }

  /**
   * Apply constraint to a transform node
   */
  applyToNode(node: TransformNode, projection: SplineProjection): void {
    node.position = projection.worldPosition;
    node.rotationQuaternion = projection.rotation;
  }

  /**
   * Get the underlying Spline instance
   */
  getSpline(): Spline {
    return this.spline;
  }

  /**
   * Get the Curve3
   */
  getCurve(): Curve3 {
    return this.spline.getCurve();
  }

  /**
   * Set a new curve
   */
  setCurve(curve: Curve3): void {
    this.spline.setCurve(curve);
  }

  /**
   * Create debug visualization for the spline
   */
  private createDebugVisualization(): void {
    this.cleanupDebugVisualization();

    const debugRenderer = DebugRenderer.getInstance();
    const points = this.spline.getCurve().getPoints();
    
    // Create path line (cyan)
    this.debugPathLine = debugRenderer.createLine(points, new Color3(0, 1, 1));
    
    // Create direction arrows at intervals (yellow)
    const totalLength = this.getTotalLength();
    const numMarkers = Math.floor(totalLength / this.markerInterval);
    
    for (let i = 0; i <= numMarkers; i++) {
      const t = i / numMarkers;
      const point = this.spline.getPointAt(t);
      const tangent = this.spline.getTangentAt(t);
      
      // Create arrow with 0.5m length for visibility
      const arrow = debugRenderer.createArrow(point, tangent, 0.5, new Color3(1, 1, 0));
      this.debugArrows.push(arrow);
    }
  }

  /**
   * Clean up debug visualization
   */
  private cleanupDebugVisualization(): void {
    const debugRenderer = DebugRenderer.getInstance();
    
    if (this.debugPathLine) {
      debugRenderer.remove(this.debugPathLine);
      this.debugPathLine = null;
    }
    
    this.debugArrows.forEach(arrow => debugRenderer.remove(arrow));
    this.debugArrows = [];
  }

  /**
   * Get marker interval
   */
  public getMarkerInterval(): number {
    return this.markerInterval;
  }

  /**
   * Set marker interval and recreate visualization
   */
  public setMarkerInterval(meters: number): void {
    this.markerInterval = meters;
    this.createDebugVisualization();
  }

  /**
   * Get spline points for visualization
   */
  public getPoints(): Vector3[] {
    return this.spline.getCurve().getPoints();
  }

  /**
   * Dispose and cleanup
   */
  public dispose(): void {
    this.cleanupDebugVisualization();
  }

  /**
   * Set a new spline (accepts either Curve3 or Spline)
   */
  setSpline(spline: Curve3 | Spline): void {
    if (spline instanceof Spline) {
      this.spline = spline;
    } else {
      this.spline = new Spline(spline);
    }
    this.createDebugVisualization();
  }

  /**
   * Project a position onto the spline path using XZ coordinates only.
   * Returns the projected XZ position on path and the distance from path.
   * 
   * @param worldPosition Current world position
   * @param lastClosestIndex Optimization hint for closest point search
   * @returns Object containing projectedXZ (Vector3 with Y=0), pathDistance (XZ distance), and closestIndex
   */
  public projectXZ(worldPosition: Vector3, lastClosestIndex = 0): {
    projectedXZ: Vector3;
    pathDistance: number;
    closestIndex: number;
  } {
    // Find closest point on spline (ignores Y coordinate)
    const { point, closestIndex } = this.spline.findClosestPoint(worldPosition, lastClosestIndex);
    
    // Calculate XZ distance from path
    const xzDelta = new Vector3(
      worldPosition.x - point.x,
      0,
      worldPosition.z - point.z
    );
    const pathDistance = xzDelta.length();
    
    // Projected position (XZ only, Y set to 0 for clarity)
    const projectedXZ = new Vector3(point.x, 0, point.z);
    
    return { projectedXZ, pathDistance, closestIndex };
  }

  /**
   * Apply spring-damper forces to pull entity toward path in XZ plane.
   * Uses isolated primitive values to prevent Vector3 reference corruption.
   * 
   * @param worldPosition Current world position (values read, not mutated)
   * @param velocity Current velocity vector (values read, not mutated)
   * @param stiffness Spring force coefficient (N/m, default 100)
   * @param damping Damping coefficient (default 10)
   * @param lastClosestIndex Optimization hint
   * @returns Force vector to apply to physics body (new Vector3 instance)
   */
  public applyPathForces(
    worldPosition: Vector3,
    velocity: Vector3,
    stiffness = 100,
    damping = 10,
    lastClosestIndex = 0
  ): { force: Vector3; closestIndex: number } {
    // Project current position onto path (XZ only)
    const { projectedXZ, closestIndex } = this.projectXZ(worldPosition, lastClosestIndex);
    
    // Calculate displacement from path (XZ only, isolated primitives)
    const displacementX = projectedXZ.x - worldPosition.x;
    const displacementZ = projectedXZ.z - worldPosition.z;
    
    // Spring force: F = -k * displacement (pulls toward path)
    const springForceX = stiffness * displacementX;
    const springForceZ = stiffness * displacementZ;
    
    // Damping force: F = -c * velocity (opposes motion perpendicular to path)
    const dampingForceX = -damping * velocity.x;
    const dampingForceZ = -damping * velocity.z;
    
    // Total force (new Vector3 to avoid reference corruption)
    const force = new Vector3(
      springForceX + dampingForceX,
      0, // No vertical constraint
      springForceZ + dampingForceZ
    );
    
    return { force, closestIndex };
  }
}
